// src/pages/Produits.jsx
import { useState, useEffect, useCallback } from 'react'
import { produitsApi, api } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Empty, Loading, fmt } from '../components/ui'


export function Produits() {
  const { toast } = useApp()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [modal, setModal] = useState(null)

  const [categories, setCategories] = useState([])
  const [catModal, setCatModal] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    produitsApi.list({ search, ...(catFilter && { categorie_id: catFilter }) })
      .then(r => setItems(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [search, catFilter])

  useEffect(() => {
    load()
    api.get('/parametres').then(r => setCategories(r.data.categories_produit || [])).catch(() => { })
  }, [load])

  async function del(id, nom) {
    if (!confirm(`Supprimer "${nom}" ?`)) return
    try { await produitsApi.delete(id); toast('Produit supprimé', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  return (
    <div className="content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="search-box">
          <i className="ti ti-search" />
          <input placeholder="Rechercher un produit…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select
          className="form-control"
          style={{ width: 200 }}
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
        >
          <option value="">Toutes les catégories</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', marginRight: 8 }}>
          <button className="btn" onClick={() => setCatModal(true)}>
            <i className="ti ti-tag" /> Catégories
          </button>
          <button className="btn primary" onClick={() => setModal({})}>
            <i className="ti ti-plus" /> Nouveau produit
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : items.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Référence</th><th>Désignation</th><th>Catégorie</th>
                  <th>Prix HT</th><th>TVA</th><th>Unité</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id}>
                    <td><code style={{ fontSize: 12 }}>{p.reference || '—'}</code></td>
                    <td>
                      <strong>{p.designation}</strong>
                      {p.description && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{p.description.substring(0, 60)}</div>}
                    </td>
                    <td>{p.categorie_nom || '—'}</td>
                    <td><strong>{fmt(p.prix_ht)}</strong></td>
                    <td>{p.tva_taux}%</td>
                    <td>{p.unite || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setModal(p)}><i className="ti ti-edit" /></button>
                        <button className="btn sm" onClick={() => del(p.id, p.designation)}>
                          <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon="package" message="Aucun produit — créez votre catalogue !" />}
      </div>

      {modal !== null && (
        <ProduitModal
          produit={modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { load(); setModal(null); toast('Produit enregistré', 'success') }}
        />
      )}
      {catModal && (
        <CategoriesModal
          categories={categories}
          onClose={() => setCatModal(false)}
          onSaved={cats => { setCategories(cats); setCatModal(false) }}
        />
      )}
    </div>
  )
}

function ProduitModal({ produit, categories = [], onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ tva_taux: 18, prix_ht: 0, avec_tva: true, ...(produit || {}) })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      if (form.id) await produitsApi.update(form.id, form)
      else await produitsApi.create(form)
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{form.id ? 'Modifier le produit' : 'Nouveau produit'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <FormGroup label="Catégorie">
              <select className="form-control" value={form.categorie_id || ''} onChange={f('categorie_id')}>
                <option value="">Sans catégorie</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </FormGroup>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}></div>
            <FormGroup label="Désignation" required>
              <input className="form-control" value={form.designation || ''} onChange={f('designation')} required />
            </FormGroup>
            <div className="grid2">
              <FormGroup label="Référence">
                <input className="form-control" value={form.reference || ''} onChange={f('reference')} />
              </FormGroup>
              <FormGroup label="Unité">
                <input className="form-control" value={form.unite || ''} onChange={f('unite')} placeholder="h, jour, unité, forfait…" />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Prix HT (€)" required>
                <input type="number" className="form-control" value={form.prix_ht ?? ''} onChange={f('prix_ht')} min={0} step="any" placeholder="0" />
              </FormGroup>
              <FormGroup label="TVA">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!form.avec_tva}
                      onChange={e => setForm(p => ({ ...p, avec_tva: e.target.checked, tva_taux: e.target.checked ? (p.tva_taux || 18) : 0 }))}
                      style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }}
                    />
                    TVA applicable
                  </label>
                </div>
                {form.avec_tva && (
                  <input
                    type="number"
                    className="form-control"
                    value={form.tva_taux ?? ''}
                    onChange={f('tva_taux')}
                    min={0}
                    step="any"
                    placeholder="18"
                  />
                )}
              </FormGroup>
            </div>
            <FormGroup label="Description">
              <textarea className="form-control" rows={2} value={form.description || ''} onChange={f('description')} />
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CategoriesModal({ categories, onClose, onSaved }) {
  const { toast } = useApp()
  const [cats, setCats] = useState(categories)
  const [newNom, setNewNom] = useState('')
  const [saving, setSaving] = useState(false)

  async function addCat() {
    if (!newNom.trim()) return
    setSaving(true)
    try {
      await api.post('/parametres', { cle: `__reload`, valeur: '1' }) // ping pour garder session
      const res = await fetch('/api/categories-produit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hs_token')}` },
        body: JSON.stringify({ nom: newNom.trim() })
      })
      const data = await res.json()
      const updated = [...cats, { id: data.data.id, nom: newNom.trim() }]
      setCats(updated)
      setNewNom('')
      toast('Catégorie ajoutée', 'success')
    } catch (e) { toast(e.message, 'error') }
    finally { setSaving(false) }
  }

  async function delCat(id) {
    if (!confirm('Supprimer cette catégorie ?')) return
    try {
      await fetch(`/api/categories-produit/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('hs_token')}` }
      })
      setCats(cats.filter(c => c.id !== id))
      toast('Catégorie supprimée', 'success')
    } catch (e) { toast(e.message, 'error') }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">Gérer les catégories</span>
          <button className="btn sm icon" onClick={() => onSaved(cats)}><i className="ti ti-x" /></button>
        </div>
        <div className="modal-body">
          {/* Liste existante */}
          <div style={{ marginBottom: 16 }}>
            {cats.length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13 }}>Aucune catégorie</p>}
            {cats.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}><i className="ti ti-tag" style={{ marginRight: 6, color: 'var(--primary)' }} />{c.nom}</span>
                <button className="btn sm" onClick={() => delCat(c.id)}>
                  <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                </button>
              </div>
            ))}
          </div>
          {/* Ajouter */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="form-control"
              placeholder="Nouvelle catégorie…"
              value={newNom}
              onChange={e => setNewNom(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCat()}
            />
            <button className="btn primary" onClick={addCat} disabled={saving || !newNom.trim()}>
              <i className="ti ti-plus" /> Ajouter
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn primary" onClick={() => onSaved(cats)}>Fermer</button>
        </div>
      </div>
    </div>
  )
}

export default Produits
